import { getSecondsInMS } from '@/utils/dates/dates';

const JWT_SEGMENT_COUNT = 3;

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** FNV-1a, for log fingerprints only — never a security boundary. */
function hashToken(token: string): string {
    let hash = FNV_OFFSET_BASIS;
    for (let index = 0; index < token.length; index += 1) {
        hash ^= token.charCodeAt(index);
        hash = Math.imul(hash, FNV_PRIME);
    }

    return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Non-sensitive token identity for logs. A slice of the token would not do: tokens from the same
 * signing key share a byte-identical header, so different sessions would print the same.
 */
export function describeSessionJwt(token: string): string {
    if (!token) return '(empty)';

    return `#${hashToken(token)} (len ${token.length}, exp ${describeExpiry(token)})`;
}

/**
 * `exp` only has to be a finite number to get this far, but `toISOString` throws outside Date's
 * ±8.64e15ms range. This runs inside recovery logging, where an unhandled throw would abort the
 * recovery before a replacement session is started — the malformed token would strand playback.
 */
function describeExpiry(token: string): string {
    const expiryMs = getSessionJwtExpiryMs(token);
    if (expiryMs === undefined) return 'none';

    const expiry = new Date(expiryMs);

    return Number.isNaN(expiry.getTime()) ? `out-of-range (${expiryMs})` : expiry.toISOString();
}

function decodeBase64Url(segment: string): string | undefined {
    // `atob` is the one base64 decoder available in both the browser and the Node SSR/test runtime.
    if (typeof atob !== 'function') return undefined;

    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');

    try {
        return atob(padded);
    } catch {
        return undefined;
    }
}

/**
 * Claims of a session JWT, or `undefined` when it is not decodable. The signature is NOT verified —
 * only the platform can do that — so this says what the token claims, not whether it is trusted.
 */
export function decodeSessionJwtClaims(token: string): Record<string, unknown> | undefined {
    const segments = token.trim().split('.');
    if (segments.length !== JWT_SEGMENT_COUNT) return undefined;

    const json = decodeBase64Url(segments[1]);
    if (!json) return undefined;

    try {
        const claims: unknown = JSON.parse(json);
        return typeof claims === 'object' && claims !== null && !Array.isArray(claims)
            ? (claims as Record<string, unknown>)
            : undefined;
    } catch {
        return undefined;
    }
}

/**
 * The `exp` claim in epoch ms, or `undefined` when absent, non-numeric, or undecodable.
 *
 * Suitable for logs and for a skew-tolerant fail-fast on a clearly expired token (see
 * `authenticateWithPlutoTV`). It is not a validity check: expiry is one flavour of invalid among
 * many that are indistinguishable client-side, so a token that passes it can still be refused, and
 * whether a token is usable remains the platform's answer to a restart
 * (see `isSessionJwtRejectedByPlatform`). Never discard a token on this alone.
 */
export function getSessionJwtExpiryMs(token: string): number | undefined {
    const exp = decodeSessionJwtClaims(token)?.exp;
    return typeof exp === 'number' && Number.isFinite(exp) ? getSecondsInMS(exp) : undefined;
}
